import type React from "react"
import { useEffect } from "react"
import { Navigate } from "react-router-dom"
import { FullPageError, FullPageLoader } from "~/components"
import { OrganizationMembershipContext } from "~/context/OrganizationMembershipContext"
import { UserContext } from "~/context/UserContext"
import { identifyUser } from "~/modules/analytics/capture"
import { setAuthToken } from "~/modules/authentication"
import { trpc } from "~/utils/trpc"
import SingleUserEmailConfirmation from "../authentication/confirmationScreens/SingleUserEmailConfirmation"
import FeideMissingEmail from "../authentication/feide/FeideMissingEmail"
import UserDoesNotHaveSubscription from "../authentication/UserDoesNotHaveSubscription"
import { MainLayout } from "./MainLayout"

type AuthenticateProps = {}

export const Authenticate: React.FC<AuthenticateProps> = () => {
  const {
    data: user,
    error,
    isLoading,
    isRefetching,
  } = trpc.users.me.useQuery(void 0, {
    retry: false,
  })

  const { data: authToken } = trpc.authentication.refreshAuthToken.useQuery(
    void 0,
    {
      enabled: !!user,
    },
  )

  useEffect(() => {
    if (!user) return

    identifyUser(user.id.toString(), {
      email: user.email,
      name: (user.firstName + " " + user.lastName).trim(),
      organization: user.organizationMemberships[0]?.organization.name,
      is_system_admin: user.role === "ADMIN",
    })
  }, [user])

  useEffect(() => {
    if (authToken) {
      setAuthToken(authToken.token)
    }
  }, [authToken])

  if (error) {
    // Check if it's a 401 error - user is not authenticated
    if ((error as any)?.data?.httpStatus === 401) {
      return <Navigate to="/login" />
    }
    return <FullPageError />
  }
  if (isLoading && !isRefetching) return <FullPageLoader />

  const loggedIn = user !== null && user !== undefined

  const organizationMembership = user?.currentOrganizationMembership ?? null

  if (user?.feideLoginMissingEmail) {
    return <FeideMissingEmail />
  }

  if (user && !user.hasActiveSubscription) {
    return (
      <UserDoesNotHaveSubscription
        user={user}
        organizationMembership={organizationMembership}
      />
    )
  }

  if (user && user.emailVerifiedAt === null) {
    return <SingleUserEmailConfirmation />
  }

  return loggedIn ? (
    <OrganizationMembershipContext.Provider value={organizationMembership}>
      <UserContext.Provider value={user}>
        <MainLayout />
      </UserContext.Provider>
    </OrganizationMembershipContext.Provider>
  ) : (
    <Navigate to="login" />
  )
}
